// React
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// @tanstack/react-query
import { useMutation, useQueryClient } from "@tanstack/react-query";

// MUI
import { GridActionsCellItem, GridRowModes, useGridApiRef } from "@mui/x-data-grid-premium";

// Third-party
import compact from "lodash-es/compact";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

// Context
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// Helpers
import RowFeedback from "@/Common/helpers/RowFeedback";
import { accountFormat } from "@/Common/helpers/number";
import { and, or } from "@/Common/helpers/sonar";

// Custom Hooks
import { useSectionMotivators } from "@Motivators/context/Common/MotivatorsSectionContext";

// Components
import MoreCell from "@/Common/components/tables/MoreCell";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import HelpTooltip from "@/Layouts/components/Tables/DataSmartTable/additional/HelpTooltip";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { dateValidation, emptyValidation } from "@/Layouts/components/Tables/DataSmartTable/validation/TableValidations";
import NotificationActionMotivator from "@/Motivators/layouts/components/NotificationActionMotivator";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DialogCell from "./components/Cells/DialogCell";
import InputEditCell from "./components/Cells/InputEditCell";
import InputEditDatetimeCell from "./components/Cells/InputEditDatetimeCell";
import InputEditMoneyCell from "./components/Cells/InputEditMoneyCell";
import InputEditPositionsCell from "./components/Cells/InputEditPositionsCell";
import InputEditSelectorCell from "./components/Cells/InputEditSelectorCell";

// Icons
import { ReactComponent as SaveIcon } from "@/Motivators/components/Spiffs/Tables/assets/icons/SaveIcon.svg";
import CancelIcon from "@mui/icons-material/Close";

// Helpers
import { getDateOnlyChangingUTC } from "@/Common/helpers/Dates";
import {
	categorizeDays,
	checkFranchisesError,
	checkGenericError,
	checkPayType,
	checkRuleError,
	checkSelectedDepartment,
	checkSelectedOperationCodes,
	checkSelectedTimeLapse,
	checkTargetedBonusDepTierError,
	checkTargetedBonusOpCodesTierError,
	checkTierError,
	defineSpiffOptions,
	defineWidthColumns,
	getRows,
} from "./helpers/spiffDefinition";

// Constants
import { columnProps } from "@/Common/constants/tableColumnProps";
import { DATE_FORMAT } from "@/Constants/date";
import { CELL_FONT_SIZE, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { HELP_TOOLTIP_TITLE } from "@/Motivators/constants";
import { INITIAL_STATE } from "./components/EligibilityRequirementsDialog/reducer";

import { extractLeafKeys } from "@/Common/GenericTree/utils";
import { DepartmentEnum } from "@/Common/constants/DepartmentEnum";
import clsx from "clsx";
import { isEmpty } from "lodash";

const variableHeightFactor = 28;

/**
 * SpiffDefinitionDataTable Component - This component is used to display the Spiff Items Data Table.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function SpiffDefinitionDataTable(props) {
	const apiRefSpiff = useGridApiRef();
	const queryClient = useQueryClient();
	const user = useSelector((state) => state.user.user);
	const newlyAddedRow = useRef(null);

	const { locationId, spiffSetup, commissionCycle, selectedItem, currentMonth, hasEditPermission, masterPermission } = props;

	// Motivators Context
	const { identifiers, mutations, subscriptions, motivatorType, isLoading } = useMotivators();
	const { setTableApiRef, setTableRows } = useSectionMotivators();

	// Rows should be passed to a state to be able to update them without side effect.
	// Mutate the cache to update the rows will work as well.
	const items = useMemo(() => getRows(spiffSetup?.items, commissionCycle), [spiffSetup?.items, commissionCycle]);
	const [rows, setRows] = useState(items);

	// Controls the edit modes using a state.
	const [rowModesModel, setRowModesModel] = useState({});

	// Controls the selected rows using a state.
	const [selectedRows, setSelectedRows] = useState([]);

	// Controls to know if some row is loading. So the add and delete buttons can be disabled.
	const [rowLoading, setRowLoading] = useState(false);

	// Row height
	const [rowHeight, setRowHeight] = useState(variableHeightFactor);

	// Notification Action when awards are gonna be removed
	const [textNotificationAction, setTextNotificationAction] = useState("");

	// This it's the correct way to listen to the edit mode changes.
	const editModeActive = useMemo(() => Object.values(rowModesModel)?.some(({ mode }) => mode === GridRowModes.Edit), [rowModesModel]);

	const department = identifiers.department;

	const [commissionCycleId, spiffId] = selectedItem ? extractLeafKeys(selectedItem._id) : [];

	/**************************************************** SUBSCRIPTIONS ****************************************************/
	const { getSubscription } = useSubscriptions();
	const spiffSetupSubscriptions = getSubscription([motivatorType + "Body", locationId, spiffId]);

	// OnCreateItem
	spiffSetupSubscriptions?.subscriptions?.onCreateItem?.setCallback((result) => {
		const spiffAdded = result.data?.[subscriptions.get("onCreateItem")?.getter]?.items[0];
		if (spiffAdded) {
			const allQueries = queryClient.getQueryCache().findAll();
			const cachesToUpdate = allQueries.filter((query) =>
				query.queryHash.includes(result.data?.[subscriptions.get("onCreateItem")?.getter]?._id)
			);

			cachesToUpdate.forEach((cache) => {
				queryClient.setQueryData(cache.queryKey, (cachedData) => {
					return {
						...cachedData,
						items: [...(cachedData?.items || []), spiffAdded],
					};
				});
			});
			cleanUpEditionMode(spiffAdded);
		}
	});

	// OnDeleteItem
	spiffSetupSubscriptions?.subscriptions?.onDeleteItem?.setCallback((result) => {
		const spiffDeleted = result.data?.[subscriptions.get("onDeleteItem")?.getter];

		// Get the row that is currently in edition mode
		const rowInEditionModeId = Object.keys(rowModesModel).find((key) => rowModesModel[key].mode === GridRowModes.Edit);
		const rowInEditionMode = rows.find((row) => row._id === rowInEditionModeId);

		const spiffId = spiffDeleted?._id;
		const items = spiffDeleted?.items;

		const allQueries = queryClient.getQueryCache().findAll();
		const cachesToUpdate = allQueries.filter((query) => query.queryHash.includes(spiffId));

		cachesToUpdate.forEach((cache) => {
			queryClient.setQueryData(cache.queryKey, (cachedData) => {
				return {
					...cachedData,
					items: cachedData?.items.filter((item) => !items.some((el) => el._id === item._id)),
				};
			});
		});

		// If the row that is currently in edition mode is deleted, clean up the edition mode
		if (rowInEditionMode && items.some((el) => el._id === rowInEditionMode._id)) {
			cleanUpEditionMode(rowInEditionMode, "delete");
		} else {
			cleanUpEditionMode();
		}
	});

	// OnUpdateItem
	spiffSetupSubscriptions?.subscriptions?.onUpdateItem?.setCallback((result) => {
		const data = result.data?.[subscriptions.get("onUpdateItem")?.getter];
		const spiffId = data?._id;
		const newItem = data?.items?.[0];

		const allQueries = queryClient.getQueryCache().findAll();
		const cachesToUpdate = allQueries.filter((query) => query.queryHash.includes(spiffId));

		cachesToUpdate.forEach((cache) => {
			queryClient.setQueryData(cache.queryKey, (cachedData) => {
				return {
					...cachedData,
					items: cachedData?.items?.map((element) => (element._id === newItem?._id ? newItem : element)),
				};
			});
		});
		cleanUpEditionMode(newItem);
	});

	const getNormalizedCommissionCycle = (value) => (value?.includes("_") ? value.split("_")[1] : value);
	/************************************************** MUTATIONS *******************************************************/
	const { mutate: updateSpiffHandler } = useMutation({
		mutationFn: ({ locationId, commissionCycleId, row }) => {
			const input = {
				title: row.title,
				spiffType: row.spiffType,
				eligibility: row.eligibility,
				effectiveDateRangeFrom: row.effectiveDateRangeFrom,
				effectiveDateRangeTo: row.effectiveDateRangeTo,
				eligibilityDays: row.eligibilityDays,
				positions: row.positions,
				requiredDealStatus: row.requiredDealStatus,
				reward: row.reward,
				updatedBy: row.updatedBy,
				type: row.type,
				description: row.description,
				transactionType: row.transactionType,
			};
			return mutations.updateItem({
				locationId,
				department,
				input,
				commissionCycleId,
				spiffSetupId: spiffId,
				spiffItemId: row?._id,
			});
		},
	});

	const { mutate: deleteSpiffHandler } = useMutation({
		mutationFn: ({ locationId, commissionCycleId, spiffId, itemIds }) =>
			mutations.deleteItem({
				locationId,
				department,
				itemIds,
				commissionCycleId,
				spiffId,
			}),
	});

	const { mutate: addSpiffHandler } = useMutation({
		mutationFn: ({ locationId, commissionCycleId, row }) =>
			mutations.createItem({
				locationId,
				department,
				commissionCycleId,
				input: {
					...row,
					spiffSetupId: spiffId,
				},
			}),
	});

	const cleanUpEditionMode = (item, operation) => {
		if (item && operation === "delete") {
			setRowModesModel((prev) => ({
				...prev,
				[item._id]: { mode: GridRowModes.View },
			}));
		}

		newlyAddedRow.current = null;
	};

	// Put the row in edit mode when the user clicks on the "Edit" button.
	const handleRowEditStart = (_params, event) => {
		if (editModeActive) {
			event.defaultPrevented = true;
			event.defaultMuiPrevented = true;
		}
	};

	// This fn is called when editing a row is stopped.
	const handleRowEditStop = (_, event) => {
		event.defaultMuiPrevented = true;
	};

	// This fn is called when the user clicks on the "Save" button.
	const handleSaveClickSpiffSetup = (idEditingRow, confirmation = false) => {
		const rowEdit = apiRefSpiff.current.state.editRows;
		const row = Object.values(rowEdit)[0];
		const keys = Object.keys(row);
		const errors = keys.filter((key) => row[key].error || row[key].value === "" || row[key].value === null);

		// If there are no errors, set the row in view mode.
		if (!errors?.length) {
			const validation = validateAwardOutEffectiveDate({ idEditingRow, editingRow: row });

			if (validation && !confirmation) {
				return;
			}

			setRowModesModel({ ...rowModesModel, [idEditingRow]: { mode: GridRowModes.View } });
		}
	};

	function validateAwardOutEffectiveDate({ idEditingRow, editingRow }) {
		const awardsEditingRow = rows.find((row) => row._id === idEditingRow)?.awards ?? [];

		if (awardsEditingRow.length > 0 && editingRow.effectiveDateRangeFrom.value && editingRow.effectiveDateRangeTo.value) {
			const effectiveFromRow = moment.utc(editingRow.effectiveDateRangeFrom.value).startOf("day");
			const effectiveToRow = moment.utc(editingRow.effectiveDateRangeTo.value).endOf("day");

			// Count individual awards that fall outside the new effective date range and will be removed
			const countAwardOutEffectiveDate = awardsEditingRow.reduce((totalCount, award) => {
				return (
					totalCount +
					(award?.awards.reduce((awardCount, innerAward) => {
						const spiffDate = moment(innerAward.spiffDate);
						const isOutsideEffectiveRange = spiffDate.isBefore(effectiveFromRow) || spiffDate.isAfter(effectiveToRow);
						return awardCount + (isOutsideEffectiveRange ? 1 : 0);
					}, 0) ?? 0)
				);
			}, 0);

			if (countAwardOutEffectiveDate > 0) {
				setTextNotificationAction(
					`Editing the following definition will remove ${countAwardOutEffectiveDate} award${
						countAwardOutEffectiveDate > 1 ? "s" : ""
					}. Do you wish to continue?`
				);

				return true;
			}
		}

		return false;
	}

	// This fn is called when the user clicks on the "Cancel" button
	const handleCancelClickSpiff = (id) => {
		setRowModesModel({
			...rowModesModel,
			[id]: { mode: GridRowModes.View, ignoreModifications: true },
		});
		setRows(items);
	};

	// This fn is called when the user clicks on the "Delete" button.
	const handleDeleteClick = async (ids, confirmation = false) => {
		const validation = validateAwardsInDefinition({ definitionsToRemove: ids });

		if (validation && !confirmation) {
			return;
		}

		setRowLoading(true);

		ids.forEach((id) => {
			RowFeedback.skeleton(id, apiRefSpiff).delete().display();
		});

		deleteSpiffHandler(
			{
				itemIds: ids,
				locationId,
				commissionCycleId,
				spiffId,
			},
			{
				onSuccess: () => {
					setRows((prev) => prev.filter((row) => !ids.includes(row._id)));
					setSelectedRows((prev) => prev.filter((id) => !ids.includes(id)));
				},
				onError: () => {
					ids.forEach((id) => {
						RowFeedback.skeleton(id, apiRefSpiff).errorDelete();
					});
				},
				onSettled: () => {
					setTextNotificationAction("");
					setRowLoading(false);
				},
			}
		);
	};

	function validateAwardsInDefinition({ definitionsToRemove }) {
		const numbersofAwards = rows
			.filter((row) => definitionsToRemove.includes(row._id) && row.awards?.length)
			.reduce((acc, row) => acc + row.awards.reduce((len, award) => len + award.awards.length, 0), 0);

		if (numbersofAwards > 0) {
			setTextNotificationAction(
				`Deleting the following definition(s) will remove ${numbersofAwards} award${
					numbersofAwards > 1 ? "s" : ""
				}. Do you wish to continue?`
			);

			return true;
		}

		return false;
	}

	// This fn is called when the user clicks on the "Add" button.
	const handleAddRow = () => {
		// define a default row initial values.
		const key = uuidv4();

		const NEW_ROW = {
			_id: key,
			isNew: true,
			updatedBy: user._id,
			title: "",
			spiffType: "Generic",
			eligibility: null,
			effectiveDateRangeFrom: null,
			effectiveDateRangeTo: null,
			eligibilityDays: [],
			requiredDealStatus: "",
			reward: 0,
		};

		newlyAddedRow.current = { ...NEW_ROW };

		// Add the new row to the rows state.
		setRows((prev) => [NEW_ROW, ...prev]);

		// Set the new row in edit mode.
		setRowModesModel((prev) => ({
			...prev,
			[key]: { mode: GridRowModes.Edit },
		}));
	};

	// This fn is called when the user clicks on the "Add" button.
	const handleRowSelected = (newSelection) => {
		setSelectedRows(newSelection);
	};

	// This fn is call after the row is added or edited, and it should be used to save data to the backend.
	const handleProcessRowUpdate = async (newRow, oldRow) => {
		// compare the old row with the new row to know if changes were made.
		const isModified = Object.keys(newRow).some((key) => newRow[key] !== oldRow[key]);
		if (!isModified) {
			return oldRow;
		}

		const updatedRow = { ...newRow, isNew: false, updatedBy: user._id };

		setRowLoading(true);
		// Here you should save the data to the backend.
		RowFeedback.skeleton(updatedRow._id, apiRefSpiff).display();

		// Mutation operations
		let result = null;
		const mutateOperation = (id, apiRef, resolve) => ({
			onSuccess: (response) => {
				resolve(response);
			},
			onSettled: () => {
				setTextNotificationAction("");
				setRowLoading(false);
				resolve(true);
			},
			onError: (err) => {
				console.log(err);
			},
		});

		if (newRow?.isNew) {
			const newRow = { ...updatedRow, createdBy: user._id };
			newlyAddedRow.current = { ...newRow };

			delete newRow.isNew;
			delete newRow._id;
			result = new Promise((resolve) => {
				addSpiffHandler(
					{
						locationId,
						row: newRow,
						commissionCycleId,
					},
					mutateOperation(updatedRow._id, apiRefSpiff, resolve)
				);
			});
		} else {
			newlyAddedRow.current = { ...updatedRow };
			result = new Promise((resolve) => {
				updateSpiffHandler(
					{
						row: updatedRow,
						locationId,
						commissionCycleId,
					},
					mutateOperation(updatedRow._id, apiRefSpiff, resolve)
				);
			});
		}

		const { items } = (await result) ?? {};
		const res = items?.[0] ?? updatedRow;

		setRows((prev) => prev.map((row) => (row._id === res._id ? res : row)));
		return res;
	};

	// This fn is call if an error was thrown when sending data to the backend.
	const handleProcessRowUpdateError = (error) => {
		// Here you should handle the error.
		console.error(error);
	};

	const { spiffSetupColumns, spiffBonusColumns } = defineWidthColumns(motivatorType);
	const { spiffSetupOptions, spiffBonusOptions } = defineSpiffOptions();

	const [selectorOpen, setSelectorOpen] = useState(false);

	const isEditable = (currentMonth, hasEditPermission) => currentMonth && hasEditPermission;

	const columnsMap = new Map([
		["_id", { field: "_id", headerName: "ID", hide: true, filterable: false }],
		["createdAt", { field: "createdAt", headerName: "Created At", hide: true, filterable: false }],
		[
			"title",
			{
				...columnProps,
				field: "title",
				headerName: "Name",
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					const { id: rowId, field } = params;
					const handleOnEditComplete = (value) => {
						// `debounceMs` is important to avoid the input to be updated too often.
						apiRefSpiff.current.setEditCellValue({ id: rowId, debounceMs: 200, value, field });
					};
					return <InputEditCell value={params.value} editComplete={handleOnEditComplete} error={params.error} />;
				},
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					// Allow only alphanumeric characters and spaces
					const regexTitle = /^[a-zA-Z0-9].*$/;
					return { ...params.props, error: !valueExists || !regexTitle.test(params.props.value) };
				},
			},
		],
		[
			"spiffType",
			{
				...columnProps,
				field: "spiffType",
				headerName: "Spiff Type",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => (
					<InputEditSelectorCell
						{...params}
						error={!params.value}
						tableApiRef={apiRefSpiff}
						options={motivatorType === "spiffSetup" ? spiffSetupOptions : spiffBonusOptions}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"eligibility",
			{
				...columnProps,
				field: "eligibility",
				headerName: "Eligibility Requirements",
				minWidth: 170,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					const { id: rowId, field, row } = params;
					const { title, spiffType } = row;
					const genericValue = params.value?.genericValue ?? "";

					const handleOnEditComplete = (value) => {
						// `debounceMs` is important to avoid the input to be updated too often.

						const editCellValue =
							!spiffType || spiffType === "Generic"
								? {
										id: rowId,
										debounceMs: 200,
										value: { ...INITIAL_STATE, genericValue: value },
										field,
								  }
								: { id: rowId, value, field };

						apiRefSpiff.current.setEditCellValue(editCellValue);
					};

					if (!spiffType || spiffType === "Generic") {
						return (
							<InputEditCell
								id={rowId}
								field={field}
								error={params.error}
								value={genericValue}
								editComplete={handleOnEditComplete}
							/>
						);
					}

					return (
						<DialogCell
							requirements={params.value}
							editModeActive={true}
							title={title}
							spiffType={spiffType}
							editComplete={handleOnEditComplete}
						/>
					);
				},
				renderCell: (params) => {
					const { title, spiffType } = params.row;
					const { id: rowId, value } = params;

					const genericValue = value?.genericValue ?? "";

					if (!spiffType || !value || spiffType === "Generic") {
						return <CustomCell value={genericValue} fontSize={CELL_FONT_SIZE} />;
					}
					const initialRow = items.find((item) => item._id === rowId);

					return (
						<DialogCell
							requirements={initialRow?.eligibility}
							editModeActive={false}
							locationId={locationId}
							title={title}
							spiffType={spiffType}
						/>
					);
				},
				preProcessEditCellProps: (params) => {
					const updatedValue = params.props.value;
					const { spiffType } = params.otherFieldsProps;

					if (!spiffType || spiffType?.value === "Generic") {
						return { ...params.props, error: checkGenericError(updatedValue?.genericValue) };
					}

					if (spiffType?.value === "Trades") {
						return {
							...params.props,
							error: checkTierError(updatedValue?.tiers) || checkFranchisesError(updatedValue?.franchises),
						};
					}

					if (spiffType?.value === "Sales") {
						return { ...params.props, error: checkTierError(updatedValue?.tiers) || checkRuleError(updatedValue?.rules) };
					}

					if (spiffType?.value === "Op Codes") {
						return {
							...params.props,
							error:
								checkTargetedBonusOpCodesTierError(updatedValue?.tiers) ||
								checkPayType(updatedValue?.selectedPayType) ||
								checkSelectedOperationCodes(updatedValue?.selectedOperationCodes) ||
								checkSelectedTimeLapse(updatedValue?.selectedTimeLapse),
						};
					}

					if (spiffType?.value === "Department") {
						return {
							...params.props,
							error:
								checkTargetedBonusDepTierError(updatedValue?.tiers) ||
								checkPayType(updatedValue?.selectedPayType) ||
								checkSelectedDepartment(updatedValue?.selectedDepartment),
						};
					}

					return { ...params.props, error: false };
				},
			},
		],
		[
			"positions",
			{
				...columnProps,
				field: "positions",
				headerName: "Position(s)",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				flex: 1.3,
				cellClassName: "!overflow-visible",
				renderEditCell: (params) => {
					return (
						<InputEditPositionsCell
							{...params}
							error={!params.value?.length}
							required
							positionType={department === "sales" ? DepartmentEnum.SALES : DepartmentEnum.SERVICE}
							onOpenAction={() => setSelectorOpen(true)}
							onCloseAction={() => setSelectorOpen(false)}
						/>
					);
				},
				renderCell: (params) => {
					if (!params.value?.length) {
						return <CustomCell value="" />;
					}
					return <MoreCell value={params.value} visibleCount={1} />;
				},
				preProcessEditCellProps: (params) => {
					const invalid = isEmpty(params.props.value);
					return { ...params.props, error: invalid };
				},
				valueGetter: (value) => {
					if (Array.isArray(value)) {
						return value;
					}
					if (typeof value === "string" && value) {
						return value.split(", ").filter(Boolean);
					}
					return [];
				},
			},
		],
		[
			"effectiveDateRangeFrom",
			{
				...columnProps,
				field: "effectiveDateRangeFrom",
				headerName: "Effective From",
				type: "date",
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					// Check if the condition is met for the custom edit cell
					const condition = !moment(params?.value).utc().endOf("day").isBefore(moment());

					if (!condition && !masterPermission) {
						// This is required to avoid the default cell to be rendered in the latest MUI version
						return undefined;
					}

					// Return the custom input component when the condition is true
					const startCommissionCycle = getDateOnlyChangingUTC(commissionCycle?.startDate);
					const today = moment();
					const effectiveDateFrom = moment(spiffSetup?.effectiveDateFrom);

					const startDate = masterPermission
						? moment.max(startCommissionCycle, effectiveDateFrom)
						: moment.max(startCommissionCycle, today, effectiveDateFrom);

					return (
						<InputEditDatetimeCell
							{...params}
							apiRef={apiRefSpiff}
							type={"from"}
							startDate={startDate}
							endDate={spiffSetup?.effectiveDateTo}
							effectiveDateRangeFrom={params?.row?.effectiveDateRangeFrom ?? null}
							effectiveDateRangeTo={params?.row?.effectiveDateRangeTo ?? null}
						/>
					);
				},
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					const valueIsDate = dateValidation(params.props.value);
					const updatedValue = moment(params.props.value);
					const effectiveDateRangeTo = params?.otherFieldsProps?.effectiveDateRangeTo.value ?? spiffSetup?.effectiveDateTo;
					const valueIsAfterFrom = updatedValue.endOf("day").isSameOrAfter(moment(spiffSetup?.effectiveDateFrom));
					const valueIsBeforeTo = updatedValue.startOf("day").isSameOrBefore(moment(effectiveDateRangeTo).endOf("day"));

					return { ...params.props, error: !valueExists || !valueIsDate || !valueIsAfterFrom || !valueIsBeforeTo };
				},
				valueFormatter: (value) => moment.utc(value).format(DATE_FORMAT.monthAbbrDatePadded),
			},
		],
		[
			"effectiveDateRangeTo",
			{
				...columnProps,
				field: "effectiveDateRangeTo",
				headerName: "Effective To",
				type: "date",
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					const startDate = masterPermission
						? moment(params.row.effectiveDateRangeFrom)
						: moment.max(moment(spiffSetup?.effectiveDateFrom), moment());
					return (
						<InputEditDatetimeCell
							{...params}
							apiRef={apiRefSpiff}
							type={"to"}
							startDate={startDate}
							endDate={spiffSetup?.effectiveDateTo}
							disabled={!moment(params.row.effectiveDateRangeFrom).isValid()}
							effectiveDateRangeFrom={params?.row?.effectiveDateRangeFrom}
							effectiveDateRangeTo={params?.row?.effectiveDateRangeTo}
						/>
					);
				},
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					const valueIsDate = dateValidation(params.props.value);
					const updatedValue = moment(params.props.value);
					const effectiveDateRangeFrom =
						params?.otherFieldsProps?.effectiveDateRangeFrom.value ?? moment(spiffSetup?.effectiveDateFrom);
					const valueIsAfterTo = updatedValue.utc().endOf("day").isSameOrAfter(effectiveDateRangeFrom);
					const valueIsBeforeTo = updatedValue
						.utc()
						.endOf("day")
						.isSameOrBefore(moment(spiffSetup?.effectiveDateTo).utc().endOf("day"));
					return { ...params.props, error: !valueExists || !valueIsDate || !valueIsAfterTo || !valueIsBeforeTo };
				},
				valueFormatter: (value) => moment.utc(value).format(DATE_FORMAT.monthAbbrDatePadded),
			},
		],
		[
			"eligibilityDays",
			{
				...columnProps,
				field: "eligibilityDays",
				headerName: "Eligible Day(s)",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					requestAnimationFrame(() => {
						const input = document.querySelectorAll(".eligibility-days");
						if (input?.length === 1) {
							setRowHeight(input[0].clientHeight + 2);
						}
					});

					return (
						<InputEditSelectorCell
							{...params}
							error={!params.value?.length}
							multiple
							multipleSelectAll
							tableApiRef={apiRefSpiff}
							popperProps={{
								sx: { minHeight: "240px", minWidth: "116px" },
							}}
							options={[
								{ label: "Monday" },
								{ label: "Tuesday" },
								{ label: "Wednesday" },
								{ label: "Thursday" },
								{ label: "Friday" },
								{ label: "Saturday" },
								{ label: "Sunday" },
							]}
							inputClasses="eligibility-days"
							onOpenAction={() => setSelectorOpen(true)}
							onCloseAction={() => setSelectorOpen(false)}
						/>
					);
				},
				renderCell: (params) => {
					if (!params.value?.length) {
						return <CustomCell value={params.value} fontSize={CELL_FONT_SIZE} />;
					}

					const categorizedDays = categorizeDays(params.value);
					return <CustomCell value={categorizedDays} hasTooltip maxWith={params.colDef.width} fontSize={CELL_FONT_SIZE} />;
				},
				preProcessEditCellProps: (params) => {
					return { ...params.props, error: params.props.value?.length === 0 };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"requiredDealStatus",
			{
				...columnProps,
				field: "requiredDealStatus",
				headerName: "Required Deals Status",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => (
					<InputEditSelectorCell
						{...params}
						error={!params.value}
						tableApiRef={apiRefSpiff}
						options={[{ label: "Delivered" }, { label: "Closed" }, { label: "Finalized" }]}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"transactionType",
			{
				...columnProps,
				field: "transactionType",
				headerName: "Transaction Type",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => (
					<InputEditSelectorCell
						{...params}
						error={!params.value}
						tableApiRef={apiRefSpiff}
						options={[{ label: "Create" }, { label: "Invoice" }, { label: "Payment" }, { label: "Close" }]}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"reward",
			{
				...columnProps,
				field: "reward",
				headerName: "Reward",
				align: "right",
				type: "number",
				cellClassName: (params) => {
					if (params.row?.spiffType === "Generic") {
						return "no-focus cell-with-dollar-symbol !px-1";
					}
					return "no-focus !px-1";
				},
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => {
					const { id: rowId, field, row } = params;
					const spiffType = row?.spiffType;

					const handleOnEditComplete = (value) => {
						const valueToSave = spiffType === "Generic" ? value : 0;
						// `debounceMs` is important to avoid the input to be updated too often.
						apiRefSpiff.current.setEditCellValue({ field, id: rowId, value: valueToSave, debounceMs: 200 });
					};

					if (spiffType === "Generic") {
						return <InputEditMoneyCell value={params.value} editComplete={handleOnEditComplete} />;
					}
					return (
						<InputEditCell id={rowId} field={field} value={"Tiers-Based"} disabled={true} editComplete={handleOnEditComplete} />
					);
				},
				preProcessEditCellProps: (params) => {
					const { spiffType } = params.otherFieldsProps;
					if (spiffType.value !== "Generic") {
						return { ...params.props, error: false };
					}
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				renderCell: (params) => {
					const spiffType = params.row?.spiffType;
					if (spiffType === "Generic") {
						return <CustomCell value={params.formattedValue ?? params.value} fontSize={CELL_FONT_SIZE} />;
					}
					return <CustomCell value={"Tiers-Based"} fontSize={CELL_FONT_SIZE} />;
				},
				valueFormatter: (value) => accountFormat(value),
			},
		],
		[
			"type",
			{
				...columnProps,
				field: "type",
				headerName: "Type",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => (
					<InputEditSelectorCell
						{...params}
						error={!params.value}
						tableApiRef={apiRefSpiff}
						options={[{ label: "Spiff" }, { label: "Bonus" }]}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"description",
			{
				...columnProps,
				field: "description",
				headerName: "Description",
				sortable: !editModeActive,
				editable: isEditable(currentMonth, hasEditPermission),
				renderEditCell: (params) => (
					<InputEditSelectorCell
						{...params}
						error={!params.value}
						options={[{ label: "Upsale" }, { label: "Repair order" }, { label: "Other" }]}
						tableApiRef={apiRefSpiff}
					/>
				),
				preProcessEditCellProps: (params) => {
					const valueExists = emptyValidation(params.props.value);
					return { ...params.props, error: !valueExists };
				},
				valueGetter: (value) => {
					if (value?.label) {
						return value.label;
					}
					return value;
				},
			},
		],
		[
			"actions",
			{
				field: "actions",
				type: "actions",
				width: 80,
				getActions: (params) => {
					const isInEditMode = rowModesModel?.[params.id]?.mode === GridRowModes.Edit;
					if (!isInEditMode) {
						return [];
					}

					return [
						<GridActionsCellItem
							key="save"
							icon={
								<SaveIcon
									className={clsx("!h-4 !w-4", {
										"!fill-default": !selectorOpen,
										"!fill-disabled": selectorOpen,
									})}
								/>
							}
							label="Save"
							onClick={() => handleSaveClickSpiffSetup(params.id)}
							color="inherit"
							disabled={selectorOpen}
						/>,
						<GridActionsCellItem
							key="cancel"
							icon={
								<CancelIcon
									className={clsx("!h-5 !w-5", {
										"!fill-default": !selectorOpen,
										"!fill-disabled": selectorOpen,
									})}
								/>
							}
							label="Cancel"
							onClick={() => handleCancelClickSpiff(params.id)}
							color="inherit"
							disabled={selectorOpen}
						/>,
					];
				},
			},
		],
	]);

	const getColumns = (typoOfMotivator) => {
		const spiffColumns = typoOfMotivator === "spiffSetup" ? spiffSetupColumns : spiffBonusColumns;
		return spiffColumns.map((column) => columnsMap.get(column.name));
	};

	const hasNonDeletableRows = () => {
		return selectedRows.some((id) => {
			const rowToDelete = rows.find((r) => r._id === id);
			return moment().isAfter(rowToDelete?.effectiveDateRangeFrom);
		});
	};

	const isCurrentMonthUTC = () => {
		const todayUTC = moment().utc();
		return moment.utc(commissionCycle?.startDate).isSameOrBefore(todayUTC) && moment(commissionCycle?.endDate).isSameOrAfter(todayUTC);
	};
	const isThereAnyAvailableDay = () => {
		const todayUTC = moment().utc().startOf("day");
		const effectiveToUTC = moment.utc(spiffSetup?.effectiveDateTo).startOf("day");
		return todayUTC.isSameOrBefore(effectiveToUTC);
	};

	const startCommissionCycleInTheFuture = () => {
		const todayUTC = moment().utc().startOf("day");
		const startCommissionCycle = moment.utc(commissionCycle?.startDate).startOf("day");
		return startCommissionCycle.isAfter(todayUTC);
	};

	useEffect(() => {
		setTableRows(compact(items));
		setRows(compact(items));
		setTableApiRef(apiRefSpiff);
		return () => {
			setRows([]);
		};
	}, [items]);

	const isComponentLoading = isLoading || rowLoading;
	const shouldDisableAddButton = () => {
		return (
			or(editModeActive, !hasEditPermission) ||
			or(
				isComponentLoading,
				(!isCurrentMonthUTC() || !isThereAnyAvailableDay()) && !startCommissionCycleInTheFuture() && !masterPermission
			)
		);
	};

	const shouldDisableDeleteButton = () =>
		(or(editModeActive, !hasEditPermission) || or(isComponentLoading, hasNonDeletableRows())) && !masterPermission;

	const variableRowHeight = useCallback(
		(rowId) => {
			if (rowId && editModeActive && Object.keys(rowModesModel)?.includes(rowId)) {
				return rowHeight;
			}
			return variableHeightFactor;
		},
		[editModeActive, rowHeight, rowModesModel]
	);

	function onCancelNotificationAction() {
		const [idEditingRow] = Object.keys(rowModesModel);
		if (idEditingRow) {
			handleCancelClickSpiff(idEditingRow);
		}
		setTextNotificationAction("");
	}

	function onAcceptNotificationAction() {
		const [idEditingRow] = Object.keys(rowModesModel);
		if (idEditingRow) {
			handleSaveClickSpiffSetup(idEditingRow, true);
		}

		if (selectedRows?.length > 0) {
			handleDeleteClick(selectedRows, true);
		}
	}

	return (
		<>
			<NotificationActionMotivator
				notification={{
					text: textNotificationAction,
					isLoading: rowLoading,
					onAccept: onAcceptNotificationAction,
					onCancel: onCancelNotificationAction,
				}}
			/>
			<DataSmartTable
				columns={getColumns(motivatorType)}
				rows={rows}
				rowHeight={28}
				apiRef={apiRefSpiff}
				editMode="row"
				maxRows={-1} // This property isn't used to limit the number of rows but we can remove it because affect the scrolling.
				getRowHeight={(params) => variableRowHeight(params?.id)}
				enableCustomScrollbars
				disableColumnMenu
				disableRowSelectionOnClick
				checkboxSelection
				emptyTableImageHeight="436px"
				isRowSelectable={(params) =>
					masterPermission ||
					(and(!editModeActive, Boolean(isEditable(currentMonth, hasEditPermission))) &&
						and(!isLoading, moment(params.row?.effectiveDateRangeFrom).isAfter(moment())))
				}
				onRowSelectionModelChange={handleRowSelected}
				rowModesModel={rowModesModel}
				processRowUpdate={handleProcessRowUpdate}
				onProcessRowUpdateError={handleProcessRowUpdateError}
				onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
				onRowEditStart={handleRowEditStart}
				onRowEditStop={handleRowEditStop}
				isCellEditable={(params) =>
					or(!moment(params.row?.effectiveDateRangeTo).isValid(), moment(params.row?.effectiveDateRangeTo).isAfter(moment())) ||
					masterPermission
				}
				columnVisibilityModel={{
					_id: false,
					createdAt: false,
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customComponent: <HelpTooltip title={HELP_TOOLTIP_TITLE} />,
						filter: {
							hide: !!selectedRows.length,
							disabled: editModeActive || isLoading,
						},
						search: {
							columns: getColumns(motivatorType),
							data: rows,
							hide: !!selectedRows.length,
							disabled: editModeActive,
						},
						add: {
							onAddAction: handleAddRow,
							hide: !!selectedRows.length,
							disabled: shouldDisableAddButton(),
						},
						delete: {
							onDeleteAction: handleDeleteClick,
							deleteTitle: "Remove Spiff",
							hide: !selectedRows.length,
							disabled: shouldDisableDeleteButton(),
						},
					},
					noRowsOverlay: {
						title: "No Spiffs Definition",
					},
				}}
				hideFooter
				customProps={{
					tableContainerStyle: { height: "100%", width: "100%" },
					tableClassName: {
						"functional-table": true,
						"edit-table": true,
						"[&_.MuiDataGrid-actionsCell]:!gap-0": true,
						"!top-[-28px]": true,
						...TAILWIND_FOR_DATA_SMART_TABLE,
					},
					hide: !rows?.length,
					tableHidedProps: props.canAddItems
						? {
								currentMonth:
									(isCurrentMonthUTC() && isThereAnyAvailableDay()) ||
									startCommissionCycleInTheFuture() ||
									masterPermission,
								sectionFirstTitle: "No Spiffs in this list.",
								onAddTheFirstOne: handleAddRow,
						  }
						: undefined,
				}}
			/>
		</>
	);
}

SpiffDefinitionDataTable.propTypes = {
	locationId: PropTypes.string,
	selectedItem: PropTypes.object,
	canAddItems: PropTypes.bool,
	spiffSetup: PropTypes.object,
	commissionCycle: PropTypes.object,
	currentMonth: PropTypes.bool,
	hasEditPermission: PropTypes.bool,
	masterPermission: PropTypes.bool,
};
